/**
 * Customer operations dashboard: the alarms/maintenance two-tab page
 * (hero stat cards, site map, and the grids) plus its CSV
 * export, which paginates the same odl_app resource the alarms and
 * resolved grids read so the export matches whatever's currently
 * filtered. The alarms tab's Status quick-filter drives active vs.
 * resolved (resolved swaps in the resolved repair-log grid in place).
 *
 * Contains:
 * - OperationsDashboardPage — default export
 */

import RestartAltIcon from '@mui/icons-material/RestartAlt';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { fetchSiteGroupsMinimalForCustomer } from '@sophocles/api/siteGroup';
import { useScope, useUser } from '@sophocles/app';
import AppNav from '@sophocles/components/App/AppNav';
import SiteMap from '@sophocles/components/map/SiteMap';
import { calcChange, filterMaintenanceByWindow, matchesSearch } from '@sophocles/features/dashboards/opsHelpers';
import {
  fetchOperationsDashboard,
  selectOperationsData,
  selectOperationsLoading,
  selectOperationsError,
} from '@sophocles/features/dashboards/slice';
import { selectAlarmCategories, selectAlarmDefinitions } from '@sophocles/features/reference';
import { useAlarmTranslation } from '@sophocles/hooks/useAlarmTranslation';
import useDebounce from '@sophocles/hooks/useDebounce';
import usePersistedState from '@sophocles/hooks/usePersistedState';
import useRelayLoadTerm from '@sophocles/hooks/useRelayLoadTerm';
import { customerMapSitePath } from '@sophocles/router/paths';
import { getCustomerTerminologies } from '@sophocles/utils/customerUtils';

import { buildOperationsFieldOverrides, getMaintenanceColumns } from './components/operationsColumns';
import OperationsGridPanel from './components/OperationsGridPanel';
import OperationsStatCardsLeft from './components/OperationsStatCardsLeft';
import OperationsStatCardsRight from './components/OperationsStatCardsRight';
import OperationsToolbar from './components/OperationsToolbar';
import SiteGroupFilterChip from './components/SiteGroupFilterChip';
import TimeFilterChip from './components/TimeFilterChip';
import {
  alarmKeysForCategories,
  alertingAlarmKeys,
  buildAlarmTreeOptions,
  categoryKeysForAlarmKeys,
} from './utils/alarmTreeOptions';
import { buildOperationsAlarmsParams, buildOperationsResolvedParams } from './utils/operationsParams';
import useOperationsExport from './utils/useOperationsExport';
import useOperationsMapSites from './utils/useOperationsMapSites';
import useOperationsQuickFilters from './utils/useOperationsQuickFilters';

function OperationsDashboardPage(props) {
  const { t } = useTranslation();
  const tAlarm = useAlarmTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const customerId = params.cust_id;
  const { customer } = useScope();
  const { user } = useUser();
  const data = useSelector(selectOperationsData);
  const loading = useSelector(selectOperationsLoading);
  const error = useSelector(selectOperationsError);
  const alarmCategories = useSelector(selectAlarmCategories);
  const alarmDefinitions = useSelector(selectAlarmDefinitions);

  const isBillboard = customer?.is_billboard ?? true;

  const terms = useMemo(() => getCustomerTerminologies({ is_billboard: isBillboard }, t), [isBillboard, t]);
  // Customer-type relay-load term ("Panels"/"Zones"/"Faces") for the Static
  // Alerts card title ("Alarming Static {{term}}").
  const relayLoadTerm = useRelayLoadTerm(undefined, customer);

  const [hours, setHours] = useState(24);
  const [siteGroups, setSiteGroups] = useState([]);
  const [siteGroupIds, setSiteGroupIds] = useState([]);
  const [selectedAlarms, setSelectedAlarms] = useState([]); // Alarm.key values (the Alarms tree filter)
  const [activeTab, setActiveTab] = useState('alarms');
  const [searchText, setSearchText] = useState('');
  const [maintenanceStatus, setMaintenanceStatus] = useState('all');
  const [maintenanceShowAll, setMaintenanceShowAll] = useState(false); // bypasses the hours window
  const [alarmStatus, setAlarmStatus] = useState('all');
  const [structureFilter, setStructureFilter] = useState(null); // 'digital' | 'static' | null
  // Live filtered total from whichever alarms-tab grid is mounted (active or
  // resolved — mutually exclusive by alarmStatus); null before its first fetch.
  const [alarmsGridRowCount, setAlarmsGridRowCount] = useState(null);

  const maintenanceApiRef = useGridApiRef();

  const debouncedSearch = useDebounce(searchText, 300);

  const [alarmsColumnVisibility, setAlarmsColumnVisibility] = usePersistedState('ops-alarms-cols', {});
  const [resolvedColumnVisibility, setResolvedColumnVisibility] = usePersistedState('ops-resolved-cols', {});
  const [maintenanceColumnVisibility, setMaintenanceColumnVisibility] = usePersistedState('ops-maintenance-cols', {});

  // Separate memoized extraParams for each EntityGrid so each refetches independently.
  const alarmsExtraParams = useMemo(
    () =>
      buildOperationsAlarmsParams({
        customerId,
        hours,
        siteGroupIds,
        selectedAlarms,
        search: debouncedSearch,
        structureFilter,
        alarmStatus,
      }),
    [customerId, hours, siteGroupIds, selectedAlarms, debouncedSearch, structureFilter, alarmStatus],
  );

  const resolvedExtraParams = useMemo(
    () =>
      buildOperationsResolvedParams({
        customerId,
        hours,
        siteGroupIds,
        selectedAlarms,
        search: debouncedSearch,
        structureFilter,
      }),
    [customerId, hours, siteGroupIds, selectedAlarms, debouncedSearch, structureFilter],
  );

  // Capture grid API and refresh from EntityGrid's onGridReady to drive the
  // shared export button and column-settings panel (both are tab-sensitive).
  const alarmsGridApiRef = useRef(null);
  const resolvedGridApiRef = useRef(null);

  useEffect(() => {
    if (!customerId) return;
    fetchSiteGroupsMinimalForCustomer(customerId, { limit: 10000 })
      .then(data => setSiteGroups(data?.results || []))
      .catch(() => {});
  }, [customerId]);

  const fetchData = useCallback(() => {
    if (customerId) {
      dispatch(fetchOperationsDashboard({ customerId, hours, siteGroupIds, textSearch: debouncedSearch }));
    }
  }, [dispatch, customerId, hours, siteGroupIds, debouncedSearch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleHoursChange = (_, newHours) => {
    setHours(newHours ?? 0);
  };

  // The map endpoint's own filter contract is category-only (a separate BE
  // surface from the alarm grids), so the selected alarm keys are collapsed
  // back to their categories here — the map narrows to every category touched
  // by the selection rather than the exact alarm set, the closest the map's
  // contract allows.
  const mapCategoryKeys = useMemo(
    () => categoryKeysForAlarmKeys(alarmDefinitions, alarmCategories, selectedAlarms),
    [alarmDefinitions, alarmCategories, selectedAlarms],
  );

  const mapFilters = useMemo(() => {
    const f = { hours };
    if (siteGroupIds.length > 0) f.siteGroupId = siteGroupIds.join(',');
    if (structureFilter) f.structureType = structureFilter;
    // Always filter map to alarm-active sites when any alarm/category filter is set
    if (mapCategoryKeys.length > 0 || structureFilter || activeTab === 'alarms') {
      f.alarmStatus = 'alarm';
    }
    if (mapCategoryKeys.length > 0) f.category = mapCategoryKeys.join(',');
    if (activeTab === 'alarms' && alarmStatus === 'resolved') f.alarmState = 'resolved';
    return f;
  }, [hours, siteGroupIds, mapCategoryKeys, structureFilter, activeTab, alarmStatus]);

  const { sites: mapSites, fitKey: mapFitKey, refit: refitMap } = useOperationsMapSites(customerId, mapFilters);

  // A marker is a shortcut into the unified map, opened on that site.
  const handleMapSiteSelect = useCallback(
    site => navigate(customerMapSitePath({ cust_id: customerId, site_id: site.id })),
    [navigate, customerId],
  );

  const handleReset = () => {
    setSiteGroupIds([]);
    setSelectedAlarms([]);
    setHours(24);
    setSearchText('');
    setMaintenanceStatus('all');
    setMaintenanceShowAll(false);
    setAlarmStatus('all');
    setStructureFilter(null);
    refitMap();
  };

  // Static/Digital is billboard vocabulary — commercial sites would all
  // read "Static", so the column and filter are billboard-only.
  const stripTypeColumn = useCallback(
    cols => (isBillboard ? cols : cols.filter(c => c.field !== 'structure_type')),
    [isBillboard],
  );

  // fieldOverrides — render overrides on descriptor columns; sizing/order stay
  // server-driven. `faces` is the one client-driven header (billboard
  // vocabulary — the customer-type relay-load term replaces the static label).
  const alarmFieldOverrides = useMemo(
    () => buildOperationsFieldOverrides({ resolved: false, customerId, tAlarm, user, t, relayLoadTerm }),
    [customerId, tAlarm, user, t, relayLoadTerm],
  );
  const resolvedFieldOverrides = useMemo(
    () => buildOperationsFieldOverrides({ resolved: true, customerId, tAlarm, user, t, relayLoadTerm }),
    [customerId, tAlarm, user, t, relayLoadTerm],
  );

  // Controlled column visibility — persist user toggle choices; non-billboard
  // always hides structure_type since Static/Digital is billboard vocabulary.
  const alarmsVisibilityModel = useMemo(
    () => (isBillboard ? alarmsColumnVisibility : { ...alarmsColumnVisibility, structure_type: false }),
    [isBillboard, alarmsColumnVisibility],
  );
  const resolvedVisibilityModel = useMemo(
    () => (isBillboard ? resolvedColumnVisibility : { ...resolvedColumnVisibility, structure_type: false }),
    [isBillboard, resolvedColumnVisibility],
  );

  const maintenanceColumns = useMemo(
    () => stripTypeColumn(getMaintenanceColumns(customerId, terms, t)),
    [customerId, terms, stripTypeColumn, t],
  );

  // Period-stat counts feed the New/Resolved Alarms card VALUES (unaffected by
  // the alarm-tree/structure-type filters — cards read the raw period totals).
  // The alarms TAB BADGE is different: it tracks whichever alarms-tab grid is
  // live (alarmsGridRowCount, reported by EntityGrid as rows arrive), so it
  // reflects the current filter set the same way the grid itself does.
  const newAlarmsCount = data?.alarm_summary?.total ?? 0;
  const resolvedCount = data?.period_stats?.cleared?.current ?? 0;
  const alarmsBadgeCount = alarmsGridRowCount ?? 0;

  const filteredMaintenance = useMemo(() => {
    let rows = data?.recent_maintenance_notes?.notes || [];
    if (siteGroupIds.length > 0) rows = rows.filter(r => siteGroupIds.includes(r.site_group_id));
    if (debouncedSearch) rows = rows.filter(r => matchesSearch(r, maintenanceColumns, debouncedSearch));
    if (maintenanceStatus === 'open') rows = rows.filter(r => r.status !== 'resolved');
    else if (maintenanceStatus === 'done') rows = rows.filter(r => r.status === 'resolved');
    rows = filterMaintenanceByWindow(rows, hours, maintenanceShowAll);
    return rows;
  }, [data, siteGroupIds, debouncedSearch, maintenanceColumns, hours, maintenanceStatus, maintenanceShowAll]);

  const maintenanceDoneCount = useMemo(
    () => filteredMaintenance.filter(r => r.status === 'resolved').length,
    [filteredMaintenance],
  );

  const alarmChartData = useMemo(() => {
    const categories = data?.alarm_summary?.categories;
    if (!categories) return [];
    return categories.map(cat => {
      const rawName = cat.name;
      // Title-case fallback if backend returns the raw short_name as the display name
      const name =
        rawName && rawName !== cat.key
          ? rawName
          : (cat.key || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      return {
        key: cat.key,
        name,
        count: cat.count,
      };
    });
  }, [data]);

  const alarmTreeOptions = useMemo(
    () => buildAlarmTreeOptions(alarmCategories, alarmDefinitions, alarmChartData, tAlarm),
    [alarmCategories, alarmDefinitions, alarmChartData, tAlarm],
  );

  // The Alarming Digital/Static cards' selection rule and the Offline Units
  // card's category-based selection, both expressed as alarm keys so every
  // card populates the same Alarms tree filter state.
  const alertingKeys = useMemo(
    () => alertingAlarmKeys(alarmDefinitions, alarmCategories),
    [alarmDefinitions, alarmCategories],
  );
  const offlineAlarmKeys = useMemo(
    () => alarmKeysForCategories(alarmDefinitions, alarmCategories, ['loss_of_power', 'offline']),
    [alarmDefinitions, alarmCategories],
  );

  const hoursLabel = hours >= 168 ? `${hours / 24}d` : `${hours}h`;
  // Trend (New/Resolved Alarms cards) is noisy under a day-scale window — show
  // it only once the lookback is a week or more.
  const showTrend = hours >= 168;

  const maintenanceOpenCount = data?.period_stats?.maintenance?.current ?? '-';

  // Period stats change calculations
  const ps = data?.period_stats;
  const offlineChange = calcChange(ps?.offline?.current, ps?.offline?.previous);
  const alarmSitesChange = calcChange(data?.alarm_summary?.total, data?.alarm_summary?.previous_total);
  const clearedChange = calcChange(ps?.cleared?.current, ps?.cleared?.previous);
  const maintenanceChange = calcChange(ps?.maintenance?.current, ps?.maintenance?.previous);
  const impactedDigitalsChange = calcChange(data?.affected_digitals?.count, data?.affected_digitals?.previous_count);
  const impactedStaticsChange = calcChange(
    data?.affected_statics?.face_count,
    data?.affected_statics?.previous_face_count,
  );

  const { exporting, handleExport } = useOperationsExport({
    activeTab,
    alarmStatus,
    filteredMaintenance,
    maintenanceColumns,
    alarmsExtraParams,
    resolvedExtraParams,
    alarmsGridApiRef,
    resolvedGridApiRef,
    customerId,
    terms,
    tAlarm,
    t,
    user,
    stripTypeColumn,
    alarmsColumnVisibility,
    resolvedColumnVisibility,
    maintenanceColumnVisibility,
  });

  const {
    quickFilters: gridQuickFilters,
    moreFilters: gridMoreFilters,
    handleResetGridFilters,
  } = useOperationsQuickFilters({
    activeTab,
    isBillboard,
    alarmStatus,
    setAlarmStatus,
    maintenanceStatus,
    setMaintenanceStatus,
    structureFilter,
    setStructureFilter,
    selectedAlarms,
    setSelectedAlarms,
    alarmTreeOptions,
    t,
  });

  return (
    <AppNav {...props} page="customer_operations_dashboard" flex notFullHeight>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: { lg: 'hidden' },
          overflowY: { xs: 'auto', lg: 'hidden' },
        }}
      >
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mx: 2, my: 1 }}>
            {String(error)}
          </Alert>
        )}

        {/* ── Sticky filter bar ── */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            bgcolor: 'var(--md-surface-container)',
            flexShrink: 0,
          }}
        >
          {/* Filter bar */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, py: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {/* Spacer */}
              <Box sx={{ flex: 1 }} />

              {/* Filters — right side */}
              <TimeFilterChip value={hours} onChange={handleHoursChange} />
              <SiteGroupFilterChip
                siteGroups={siteGroups}
                selectedIds={siteGroupIds}
                onChange={setSiteGroupIds}
                label={terms.siteGroup}
              />

              <Tooltip title={t('operationsDashboard.resetViewsFilters', { defaultValue: 'Reset Views & Filters' })}>
                <IconButton size="small" onClick={handleReset}>
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* ── Page body — single gap token controls all section spacing ── */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            gap: 'var(--page-gap)',
            px: 'var(--page-gap)',
            pt: 'var(--page-gap)',
            pb: 'var(--page-gap)',
          }}
        >
          {/* ── Hero: Left Cards + Map + Right Cards ── */}
          <Box
            sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, flexShrink: 0, gap: 'var(--page-gap)' }}
          >
            <OperationsStatCardsLeft
              t={t}
              data={data}
              loading={loading}
              hoursLabel={hoursLabel}
              newAlarmsCount={newAlarmsCount}
              alarmSitesChange={alarmSitesChange}
              resolvedCount={resolvedCount}
              clearedChange={clearedChange}
              activeTab={activeTab}
              selectedAlarms={selectedAlarms}
              structureFilter={structureFilter}
              alarmStatus={alarmStatus}
              setActiveTab={setActiveTab}
              setSelectedAlarms={setSelectedAlarms}
              setStructureFilter={setStructureFilter}
              setAlarmStatus={setAlarmStatus}
              showTrend={showTrend}
            />

            {/* Map Card — stretches to fill card column height on desktop */}
            <Box
              sx={{
                position: 'relative',
                flex: 1,
                minHeight: 220,
                height: { xs: 220, lg: 'auto' },
                alignSelf: 'stretch',
              }}
            >
              <Card
                elevation={0}
                variant="outlined"
                sx={{
                  height: '100%',
                  width: '100%',
                  borderRadius: 'var(--radius-lg)',
                  overflow: 'hidden',
                  position: 'relative',
                  zIndex: 0,
                  isolation: 'isolate',
                }}
              >
                {loading && !data && (
                  <Skeleton variant="rectangular" sx={{ position: 'absolute', inset: 0, zIndex: 1 }} />
                )}
                <SiteMap
                  sites={mapSites}
                  isBillboard={isBillboard}
                  onSelectSite={handleMapSiteSelect}
                  fitKey={mapFitKey}
                  popoverHours={hours}
                  showTerminator
                />
              </Card>
            </Box>

            <OperationsStatCardsRight
              t={t}
              data={data}
              loading={loading}
              hoursLabel={hoursLabel}
              resolvedCount={resolvedCount}
              newAlarmsCount={newAlarmsCount}
              clearedChange={clearedChange}
              offlineChange={offlineChange}
              maintenanceChange={maintenanceChange}
              maintenanceOpenCount={maintenanceOpenCount}
              maintenanceDoneCount={maintenanceDoneCount}
              alarmSitesChange={alarmSitesChange}
              impactedDigitalsChange={impactedDigitalsChange}
              impactedStaticsChange={impactedStaticsChange}
              activeTab={activeTab}
              selectedAlarms={selectedAlarms}
              structureFilter={structureFilter}
              alarmStatus={alarmStatus}
              alertingKeys={alertingKeys}
              offlineAlarmKeys={offlineAlarmKeys}
              relayLoadTerm={relayLoadTerm}
              setActiveTab={setActiveTab}
              setSelectedAlarms={setSelectedAlarms}
              setStructureFilter={setStructureFilter}
              setAlarmStatus={setAlarmStatus}
              showTrend={showTrend}
            />
          </Box>

          {/* ── Toolbar (separate card) ── */}
          <OperationsToolbar
            t={t}
            activeTab={activeTab}
            onTabChange={(_, v) => setActiveTab(v)}
            alarmsBadgeCount={alarmsBadgeCount}
            maintenanceBadgeCount={filteredMaintenance.length}
            searchText={searchText}
            onSearchChange={e => setSearchText(e.target.value)}
            quickFilters={gridQuickFilters}
            moreFilters={gridMoreFilters}
            onResetAll={handleResetGridFilters}
            maintenanceExtra={
              activeTab === 'maintenance' && hours > 0 ? (
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={maintenanceShowAll}
                      onChange={e => setMaintenanceShowAll(e.target.checked)}
                    />
                  }
                  label={t('operationsDashboard.showAllMaintenance', { defaultValue: 'Show all' })}
                  sx={{ mr: 0.5, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                />
              ) : null
            }
            exporting={exporting}
            onExport={handleExport}
            onShowColumnSettings={() => {
              const ref =
                activeTab === 'alarms'
                  ? alarmStatus === 'resolved'
                    ? resolvedGridApiRef.current
                    : alarmsGridApiRef.current
                  : activeTab === 'maintenance'
                    ? maintenanceApiRef
                    : null;
              if (ref?.current?.showPreferences) ref.current.showPreferences('columns');
            }}
          />

          {/* ── Grid (border + radius come from the global MuiDataGrid theme) ── */}
          <Box sx={{ flex: 1, minHeight: { xs: 400, lg: 0 }, display: 'flex', flexDirection: 'column' }}>
            <OperationsGridPanel
              activeTab={activeTab}
              alarmStatus={alarmStatus}
              customerId={customerId}
              navigate={navigate}
              alarmFieldOverrides={alarmFieldOverrides}
              alarmsExtraParams={alarmsExtraParams}
              alarmsVisibilityModel={alarmsVisibilityModel}
              setAlarmsColumnVisibility={setAlarmsColumnVisibility}
              alarmsGridApiRef={alarmsGridApiRef}
              resolvedFieldOverrides={resolvedFieldOverrides}
              resolvedExtraParams={resolvedExtraParams}
              resolvedVisibilityModel={resolvedVisibilityModel}
              setResolvedColumnVisibility={setResolvedColumnVisibility}
              resolvedGridApiRef={resolvedGridApiRef}
              onAlarmsBadgeCountChange={setAlarmsGridRowCount}
              maintenanceApiRef={maintenanceApiRef}
              filteredMaintenance={filteredMaintenance}
              maintenanceColumns={maintenanceColumns}
              maintenanceColumnVisibility={maintenanceColumnVisibility}
              setMaintenanceColumnVisibility={setMaintenanceColumnVisibility}
            />
          </Box>
        </Box>
      </Box>
    </AppNav>
  );
}

export default OperationsDashboardPage;
